const TinyPumperStatus = {
    Good: 1,
    Warning: 2,
    Critical: 3,
    NA: 4,
    1: 'Good',
    2: 'Warning',
    3: 'Critical',
    4: 'N/A'
};

const TinyPumperIncident = {
    CellularIssue: 1,
    BLEIssue: 2,
    FaultyCapacitor: 3,
    LowBattery: 4,
    TankSensorCalibration: 5,
    DifferenceLevelconVsGreaseBook: 6,
    SensorDisconnected: 7,
    SensorCalibration: 8,
    SensorIssue: 9,
    1: 'Cellular Issue',
    2: 'BLE Issue',
    3: `
        Faulty Capacitor
        
        - 'Battery Level' (ie Solar Strength) peaks above a 4.0 each day but 'Capacitor' level never rises beyond 2.0 for 2 days in a row.
        - This signifies a blown or faulty Capacitor that must be replaced.
    `,
    4: `
        Low Battery
        
        - Located in the place in which you installed it
        - Solar panel is wiped clean 
        - Solar panel has an unobstructed view of the sky (not the horizon…)

        When reviewing your TinyPumper, please note:
        - Single flash every 8 seconds = healthy 
        - Double flash = low batteries
        - Triple flash = monitor issue
        
        Please note: flashes only visible on TinyPumper, NOT Sensors.
    `,
    5: `
        Tank Sensor Calibration
        
        Your radar tank sensor is unable to calibrate itself due to one of the following issues:
        - Tank is more than 20 feet tall.
        - There is less than 12 inches between the tank port and the wall of tank.
        - The tank port where Sensor is installed is sloped at an angle (not flat with the roof of tank…)
        - The reducer you purchased is made from metal, NOT PVC / plastic.
        - The reducer you purchased has a riser pipe, neck, or extra connector that is more than 2” in height.
        - Radar Tank Sensor or reducer protrudes down into the tank.
    `,
    6: `
        Difference Levelcon Vs GreaseBook

        - LevelCon reports a Tank Gauge spike more than the tank height for several consecutive measurements and Greasebook copies and repeats the last valid gauge.
    `,
    7: `
        Sensor Disconnected

        - It appears sensor has become disconnected from your TinyPumper. Please reattach the sensor.
    `,
    8: `
        Sensor Calibration
    
        - TEMPERATURE staying flat/constant signals device needs to be reset
        - MEASUREMENT staying PRECISELY flat/constant signals device needs to be reset: 168.21, 168.21. 168.21. 
            - Normal operation would be: 168.20, 168,23. 168.19.
            - Next time at sensor, please remove batteries and pop back in to reset your sensor calibration.
    `,
    9: `
        Sensor issue

        - Check the Modbus data table on the portal. This is the only way to check the status of the (F100)RS232. 
        If there is a string of zeros after a valid reading of the slave address and register, then the monitor is able to connect with the meter, but is 
        getting garbage for return values. 
        For example: 0199B7140058C21400F200000000000000000000000000000000000000000000000000000000000000000000
        
        - Or, if the meter only comes back with this, then it is able to make a connection but cannot ready any registers to get good or bad data.
        For example: 0199B700.
        On the other hand,if the data looks like this, then we are getting a good data return on the query. 
        0199B7146342B866C940FB70A742BCBE6941DCB3F940DAC85A41A0B05548257B5E41A0D904407F6AC03E0000
    `
};

// ---------------------------------------------------------------------------------------------
// Incident engine (dbo.SensorIncident). Values mirror GreaseBook.Domain.Enums.SensorIncident* and
// GreaseBook.AdminPortal.Entities.Enums — change one, change all three.
// ---------------------------------------------------------------------------------------------

const SensorIncidentScope = Object.freeze({
    Sensor: 1,
    Gateway: 2,
    Company: 3,
    Vendor: 4
});

const SENSOR_INCIDENT_SCOPE_LABELS = Object.freeze({
    [SensorIncidentScope.Sensor]: 'Sensor',
    [SensorIncidentScope.Gateway]: 'Gateway',
    [SensorIncidentScope.Company]: 'Company',
    [SensorIncidentScope.Vendor]: 'Vendor'
});

const SensorIncidentSeverity = Object.freeze({
    Low: 1,
    Medium: 2,
    High: 3,
    Critical: 4
});

const SENSOR_INCIDENT_SEVERITY_LABELS = Object.freeze({
    [SensorIncidentSeverity.Low]: 'Low',
    [SensorIncidentSeverity.Medium]: 'Medium',
    [SensorIncidentSeverity.High]: 'High',
    [SensorIncidentSeverity.Critical]: 'Critical'
});

const SensorIncidentCause = Object.freeze({
    Unknown: 0,
    DeviceOrField: 1,
    GatewayDown: 2,
    SiteWide: 3,
    VendorOutage: 4,
    BatteryDepleted: 5,
    IngestionFailure: 6,
    VendorReportingLag: 7
});

const SENSOR_INCIDENT_CAUSE_LABELS = Object.freeze({
    [SensorIncidentCause.Unknown]: 'Unknown',
    [SensorIncidentCause.DeviceOrField]: 'Device or field',
    [SensorIncidentCause.GatewayDown]: 'Gateway down',
    [SensorIncidentCause.SiteWide]: 'Site-wide',
    [SensorIncidentCause.VendorOutage]: 'LevelCon outage',
    [SensorIncidentCause.BatteryDepleted]: 'Battery depleted',
    [SensorIncidentCause.IngestionFailure]: 'Ingestion failure',
    [SensorIncidentCause.VendorReportingLag]: 'LevelCon holding the reading'
});

const SensorIncidentKind = Object.freeze({
    LevelconSilent: 1,
    GreaseBookSyncLag: 2,
    VendorOutage: 3
});

const SENSOR_INCIDENT_KIND_LABELS = Object.freeze({
    [SensorIncidentKind.LevelconSilent]: 'Silent on LevelCon',
    [SensorIncidentKind.GreaseBookSyncLag]: 'Not ingested by GreaseBook',
    [SensorIncidentKind.VendorOutage]: 'LevelCon not delivering'
});

const SensorSnoozeReason = Object.freeze({
    WaitingOnClient: 1,
    BatteryReplacement: 2,
    PendingDecommission: 3,
    SeasonalShutIn: 4,
    InstallInProgress: 5,
    Other: 6,
    VisitScheduled: 7
});

const SENSOR_SNOOZE_REASON_LABELS = Object.freeze({
    [SensorSnoozeReason.WaitingOnClient]: 'Waiting on client',
    [SensorSnoozeReason.BatteryReplacement]: 'Battery replacement',
    [SensorSnoozeReason.PendingDecommission]: 'Pending decommission',
    [SensorSnoozeReason.SeasonalShutIn]: 'Seasonal shut-in',
    [SensorSnoozeReason.InstallInProgress]: 'Install in progress',
    [SensorSnoozeReason.Other]: 'Other',
    [SensorSnoozeReason.VisitScheduled]: 'Visit scheduled'
});

const toSelectOptions = labels => Object.entries(labels).map(([value, text]) => ({ value: Number(value), text }));

export {
    TinyPumperStatus,
    TinyPumperIncident,
    SensorIncidentScope,
    SENSOR_INCIDENT_SCOPE_LABELS,
    SensorIncidentSeverity,
    SENSOR_INCIDENT_SEVERITY_LABELS,
    SensorIncidentCause,
    SENSOR_INCIDENT_CAUSE_LABELS,
    SensorIncidentKind,
    SENSOR_INCIDENT_KIND_LABELS,
    SensorSnoozeReason,
    SENSOR_SNOOZE_REASON_LABELS,
    toSelectOptions
};
